import type { Enrollment, Note, Submission } from "../../../proto/qf/types_pb"
import type { GroupsByID } from "../../Helpers"
import { enrollmentGroup } from "../../Helpers"
import type { NoteTarget } from "../../overmind/namespaces/notes/actions"

/** A labelled target the staff member may attach a new note to. */
export type LabelledTarget = { key: string, label: string, value: NoteTarget }

/** A short description of which entity a note is attached to. */
export type TargetInfo = { icon: string, text: string }

export const noteTargetKey = (target: NoteTarget): string => {
    if (target.SubmissionID && target.SubmissionID > 0n) {
        return `submission:${target.SubmissionID}`
    }
    if (target.GroupID && target.GroupID > 0n) {
        return `group:${target.GroupID}`
    }
    if (target.EnrollmentID && target.EnrollmentID > 0n) {
        return `enrollment:${target.EnrollmentID}`
    }
    return "none"
}

const labelledTarget = (label: string, value: NoteTarget): LabelledTarget => ({
    key: noteTargetKey(value),
    label,
    value,
})

export const submissionNoteTargets = (submission: Submission, enrollments: Enrollment[], groups?: GroupsByID): LabelledTarget[] => {
    const enrollmentByUserID = new Map(enrollments.map(enrollment => [enrollment.userID, enrollment]))
    const targets: LabelledTarget[] = [labelledTarget("This submission", { SubmissionID: submission.ID })]

    if (submission.groupID > 0n) {
        const group = groups?.get(submission.groupID)
        targets.push(labelledTarget(group ? `Group: ${group.name}` : "Group", { GroupID: submission.groupID }))
        group?.users.forEach(user => {
            const enrollment = enrollmentByUserID.get(user.ID)
            if (enrollment) {
                targets.push(labelledTarget(`Student: ${user.Name}`, { EnrollmentID: enrollment.ID }))
            }
        })
        return targets
    }

    if (submission.userID > 0n) {
        const enrollment = enrollmentByUserID.get(submission.userID)
        if (enrollment) {
            targets.push(labelledTarget(enrollment.user ? `Student: ${enrollment.user.Name}` : "Student", { EnrollmentID: enrollment.ID }))
            const group = enrollment.groupID > 0n ? groups?.get(enrollment.groupID) : undefined
            if (group) {
                targets.push(labelledTarget(`Group: ${group.name}`, { GroupID: group.ID }))
            }
        }
    }
    return targets
}

export const studentNoteTargets = (enrollment: Enrollment, groups?: GroupsByID): LabelledTarget[] => {
    const targets = [labelledTarget("Student", { EnrollmentID: enrollment.ID })]
    if (enrollment.groupID > 0n) {
        const group = enrollmentGroup(enrollment, groups)
        targets.push(labelledTarget(group?.name ? `Group: ${group.name}` : "Group", { GroupID: enrollment.groupID }))
    }
    return targets
}

export const notesForEnrollment = (notes: Note[], enrollment: Enrollment): Note[] => notes.filter(note =>
    note.EnrollmentID === enrollment.ID || (enrollment.groupID > 0n && note.GroupID === enrollment.groupID)
)

/** noteCountsByEnrollment returns the number of notes relevant to each enrollment,
    counting the enrollment's own notes and the notes on its group. Enrollments
    without notes are left out of the map.
    The notes are indexed by target in a single pass, so the cost is linear in the
    number of notes and enrollments rather than the product of the two. */
export const noteCountsByEnrollment = (notes: Note[], enrollments: Enrollment[]): Map<bigint, number> => {
    const byEnrollment = new Map<bigint, number>()
    const byGroup = new Map<bigint, number>()
    for (const note of notes) {
        // A note has exactly one target, enforced by Note.IsValid() server-side.
        if (note.EnrollmentID > 0n) {
            byEnrollment.set(note.EnrollmentID, (byEnrollment.get(note.EnrollmentID) ?? 0) + 1)
        } else if (note.GroupID > 0n) {
            byGroup.set(note.GroupID, (byGroup.get(note.GroupID) ?? 0) + 1)
        }
    }

    const counts = new Map<bigint, number>()
    for (const enrollment of enrollments) {
        const own = byEnrollment.get(enrollment.ID) ?? 0
        const group = enrollment.groupID > 0n ? byGroup.get(enrollment.groupID) ?? 0 : 0
        if (own + group > 0) {
            counts.set(enrollment.ID, own + group)
        }
    }
    return counts
}

export const submissionNoteTargetInfo = (note: Note, enrollments: Enrollment[], groups?: GroupsByID): TargetInfo => {
    const enrollmentByID = new Map(enrollments.map(enrollment => [enrollment.ID, enrollment]))
    if (note.EnrollmentID > 0n) {
        return { icon: "fa-user", text: enrollmentByID.get(note.EnrollmentID)?.user?.Name ?? "Student" }
    }
    if (note.GroupID > 0n) {
        return { icon: "fa-users", text: groups?.get(note.GroupID)?.name ?? "Group" }
    }
    return { icon: "fa-file-lines", text: "Submission" }
}

export const studentNoteTargetInfo = (note: Note, enrollment: Enrollment, groups?: GroupsByID): TargetInfo => {
    if (note.GroupID > 0n) {
        return { icon: "fa-users", text: groups?.get(note.GroupID)?.name ?? "Group" }
    }
    return { icon: "fa-user", text: enrollment.user?.Name ?? "Student" }
}
